import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Activity,
  HardDrive,
  List,
  Layers,
  Loader2,
  ShieldCheck,
  Server,
  Radio,
  FileText,
  CheckCircle2,
  Clock,
  Zap,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface OverviewViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const fmtSize = (mb: number | null | undefined) => {
  if (mb == null || mb <= 0) return '—';
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(mb * 1024).toFixed(0)} KB`;
};

const fmtCompact = (n: number | null | undefined) => {
  if (n == null) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const OverviewView: React.FC<OverviewViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType,
}) => {
  const [loading, setLoading] = useState(true);
  const [metricsData, setMetricsData] = useState<any>(null);
  const [connGraphData, setConnGraphData] = useState<any>(null);
  // const [hostGraphData, setHostGraphData] = useState<any>(null);
  const [queryThroughputData, setQueryThroughputData] = useState<any>(null);
  const [queryDurationData, setQueryDurationData] = useState<any>(null);
  const [overviewDbTable, setOverviewDbTable] = useState<any[]>([]);

  // Legend Hover States per Chart
  const [hoveredConn, setHoveredConn] = useState<string | null>(null);
  // const [hoveredCpu, setHoveredCpu] = useState<string | null>(null);
  const [hoveredThroughput, setHoveredThroughput] = useState<string | null>(null);
  const [hoveredDuration, setHoveredDuration] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60');

    const baseParams: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) baseParams.client_id = clientId;

    const safeGet = async (url: string, params: any) => {
      try {
        const res = await baseapi.get(url, { params });
        return res.data?.data || res.data;
      } catch (e) {
        return null;
      }
    };

    try {
      const [
        mRes,
        connRes,
        // hostRes,
        qtRes,
        qdRes,
        perDbRes,
      ] = await Promise.all([
        safeGet('/db_monitor_dashboard/metrics', { cluster_id: activeCluster, client_id: clientId }),
        safeGet('/db_monitor_dashboard/graph/connections', baseParams),
        // safeGet('/db_monitor_dashboard/graph/host', baseParams),
        safeGet('/db_monitor_dashboard/graph/query_throughput', baseParams),
        safeGet('/db_monitor_dashboard/graph/query_duration', baseParams),
        safeGet('/db_monitor_dashboard/per_db/overview', { cluster_id: activeCluster, client_id: clientId }),
      ]);

      setMetricsData(mRes);
      setConnGraphData(connRes);
      // setHostGraphData(hostRes);
      setQueryThroughputData(qtRes);
      setQueryDurationData(qdRes);

      const tableData = perDbRes?.data || (Array.isArray(perDbRes) ? perDbRes : []);
      setOverviewDbTable(tableData);
    } catch (err) {
      console.warn('OverviewView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Helper to format unique time labels per dataset
  const formatTimeLabels = (labels: string[]) => {
    if (!labels || labels.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return labels.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  // Transform graph series into Recharts format
  const formatSeries = (labels: string[], seriesMap: { [key: string]: number[] }) => {
    if (!labels || labels.length === 0) return [];
    const timeLabels = formatTimeLabels(labels);
    return labels.map((_, idx) => {
      const point: any = { time: timeLabels[idx] };
      Object.keys(seriesMap || {}).forEach((key) => {
        const val = seriesMap[key]?.[idx];
        point[key] = val !== undefined && val !== null ? Number(val.toFixed(2)) : 0;
      });
      return point;
    });
  };

  const connDataFormatted = formatSeries(connGraphData?.labels || [], connGraphData?.series || {});
  // const hostDataFormatted = formatSeries(hostGraphData?.labels || [], hostGraphData?.series || {});

  const throughputTimeLabels = formatTimeLabels(queryThroughputData?.labels || []);
  const throughputFormatted = (queryThroughputData?.labels || []).map((_: string, idx: number) => {
    const point: any = { time: throughputTimeLabels[idx] };
    (queryThroughputData?.query_types || []).forEach((qt: string) => {
      point[qt.toUpperCase()] = Number((queryThroughputData?.series?.[qt]?.[idx] || 0).toFixed(2));
    });
    return point;
  });

  const durationTimeLabels = formatTimeLabels(queryDurationData?.labels || []);
  const durationFormatted = (queryDurationData?.labels || []).map((_: string, idx: number) => {
    const point: any = { time: durationTimeLabels[idx] };
    (queryDurationData?.query_types || []).forEach((qt: string) => {
      point[qt.toUpperCase()] = Number((queryDurationData?.series?.[qt]?.[idx] || 0).toFixed(2));
    });
    return point;
  });

  const colors = ['#3b82f6', '#10b981', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4'];

  const hasData = (arr: any[]) => {
    if (!arr || arr.length === 0) return false;
    return arr.some(item => Object.keys(item).some(k => k !== 'time' && typeof item[k] === 'number' && item[k] > 0));
  };

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const isMongoDB = ['mongodb', 'mongo'].includes((dbType || '').toLowerCase());
  const health = metricsData?.cluster_health || 'healthy';
  const role = metricsData?.actual_role || 'primary';
  const cacheHit = metricsData?.cache_hit_pct;

  const totalDocs = useMemo(() => {
    if (!Array.isArray(overviewDbTable)) return 0;
    return overviewDbTable.reduce((s: number, r: any) => s + (r.docs_count || 0), 0);
  }, [overviewDbTable]);

  const totalDataSizeMB = useMemo(() => {
    if (!Array.isArray(overviewDbTable)) return metricsData?.database_size_mb || 0;
    const sum = overviewDbTable.reduce((s: number, r: any) => s + (r.size_mb || 0), 0);
    return sum > 0 ? sum : metricsData?.database_size_mb || 0;
  }, [overviewDbTable, metricsData]);

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-[2px] z-20 flex items-center justify-center rounded-xl min-h-[400px]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-3 relative min-h-[400px]">
      {/* 10 MongoDB Stat Tiles for Mongo */}
      {isMongoDB ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
          {/* Health */}
          <div className="bg-gradient-to-br from-emerald-950/30 to-gray-900/80 border border-emerald-500/30 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Cluster Health</span>
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
              {health}
            </span>
          </div>

          {/* Role */}
          <div className="bg-gradient-to-br from-blue-950/30 to-gray-900/80 border border-blue-500/30 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Node Role</span>
              <Server className="w-3.5 h-3.5 text-blue-400" />
            </div>
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-500/20 text-blue-400 border border-blue-500/40">
              {role}
            </span>
          </div>

          {/* Cluster Nodes */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Cluster Nodes</span>
              <Radio className="w-3.5 h-3.5 text-gray-400" />
            </div>
            <div className="text-base font-bold font-mono text-white">
              {metricsData?.cluster_nodes_online ?? '—'}{' '}
              <span className="text-xs text-gray-400 font-normal">/ {metricsData?.cluster_nodes_total ?? '—'}</span>
            </div>
          </div>

          {/* Total Documents */}
          <div className="bg-gradient-to-br from-purple-950/30 to-gray-900/80 border border-purple-500/30 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Total Documents</span>
              <FileText className="w-3.5 h-3.5 text-purple-400" />
            </div>
            <div className="text-base font-bold font-mono text-purple-300">{fmtCompact(totalDocs)}</div>
          </div>

          {/* Data Size */}
          <div className="bg-gradient-to-br from-amber-950/30 to-gray-900/80 border border-amber-500/30 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Data Size</span>
              <HardDrive className="w-3.5 h-3.5 text-amber-400" />
            </div>
            <div className="text-base font-bold font-mono text-amber-300">{fmtSize(totalDataSizeMB)}</div>
          </div>

          {/* Disk Free */}
          <div className="bg-gradient-to-br from-cyan-950/30 to-gray-900/80 border border-cyan-500/30 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Disk Free</span>
              <HardDrive className="w-3.5 h-3.5 text-cyan-400" />
            </div>
            <div className="text-base font-bold font-mono text-cyan-300">{fmtSize(metricsData?.disk_free_mb)}</div>
          </div>

          {/* Cache Hit */}
          <div className="bg-gradient-to-br from-emerald-950/30 to-gray-900/80 border border-emerald-500/30 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Cache Hit %</span>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <div className="text-base font-bold font-mono text-emerald-400">
              {cacheHit != null ? `${Number(cacheHit).toFixed(1)}%` : '—'}
            </div>
          </div>

          {/* Connected Clients */}
          <div className="bg-gradient-to-br from-blue-950/30 to-gray-900/80 border border-blue-500/30 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Connected Clients</span>
              <Activity className="w-3.5 h-3.5 text-blue-400" />
            </div>
            <div className="text-base font-bold font-mono text-blue-400">
              {metricsData?.connections_total ?? '—'}
            </div>
          </div>

          {/* Oplog Window */}
          <div className="bg-gradient-to-br from-orange-950/30 to-gray-900/80 border border-orange-500/30 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Oplog Window</span>
              <Clock className="w-3.5 h-3.5 text-orange-400" />
            </div>
            <div className="text-base font-bold font-mono text-orange-300">
              {metricsData?.oplog_window_hours == null
                ? '—'
                : metricsData.oplog_window_hours >= 24
                  ? `${(metricsData.oplog_window_hours / 24).toFixed(1)}d`
                  : `${Number(metricsData.oplog_window_hours).toFixed(1)}h`}
            </div>
          </div>

          {/* Replication Lag */}
          <div className="bg-gradient-to-br from-rose-950/30 to-gray-900/80 border border-rose-500/30 rounded-xl p-2.5 shadow-md">
            <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold mb-1">
              <span>Repl Lag</span>
              <Zap className="w-3.5 h-3.5 text-rose-400" />
            </div>
            <div className="text-base font-bold font-mono text-rose-400">
              {metricsData?.replication_lag_sec == null ? '—' : `${Number(metricsData.replication_lag_sec).toFixed(1)}s`}
            </div>
          </div>
        </div>
      ) : (
        /* Status Health Indicators Bar for standard DBs */
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-3 flex flex-wrap items-center gap-3 text-xs shadow-lg">
          <span className="font-semibold text-white flex items-center gap-1.5 mr-2">
            <Activity className="w-4 h-4 text-blue-400" /> Health Monitors
          </span>

          {(() => {
            if (!metricsData) {
              return (
                <div className="bg-gray-900/60 border border-gray-700 px-2.5 py-0.5 rounded-lg flex items-center gap-2">
                  <span className="text-gray-400">CPU</span>
                  <span className="font-bold text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded text-[10px]">
                    1 OK
                  </span>
                </div>
              );
            }
            const userVal = metricsData.host_cpu_user_pct;
            const sysVal = metricsData.host_cpu_sys_pct;
            const user = userVal !== undefined && userVal !== null ? Number(userVal) : NaN;
            const sys = sysVal !== undefined && sysVal !== null ? Number(sysVal) : NaN;
            if (isNaN(user) && isNaN(sys)) {
              return (
                <div className="bg-gray-900/60 border border-gray-700 px-2.5 py-0.5 rounded-lg flex items-center gap-2">
                  <span className="text-gray-400">CPU</span>
                  <span className="font-bold text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded text-[10px]">
                    1 OK
                  </span>
                </div>
              );
            }
            const total = (isNaN(user) ? 0 : user) + (isNaN(sys) ? 0 : sys);
            const isHigh = total > 90;
            return (
              <div className="bg-gray-900/60 border border-gray-700 px-2.5 py-0.5 rounded-lg flex items-center gap-2">
                <span className="text-gray-400">CPU</span>
                <span
                  className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${
                    isHigh
                      ? 'text-red-400 bg-red-500/15 border border-red-500/30'
                      : 'text-green-400 bg-green-500/10'
                  }`}
                >
                  {total.toFixed(1)}%
                </span>
              </div>
            );
          })()}

          {(() => {
            if (!metricsData) {
              return (
                <div className="bg-gray-900/60 border border-gray-700 px-2.5 py-0.5 rounded-lg flex items-center gap-2">
                  <span className="text-gray-400">Memory</span>
                  <span className="font-bold text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded text-[10px]">
                    1 OK
                  </span>
                </div>
              );
            }
            const memVal = metricsData.host_mem_used_pct;
            const mem = memVal !== undefined && memVal !== null ? Number(memVal) : NaN;
            if (isNaN(mem)) {
              return (
                <div className="bg-gray-900/60 border border-gray-700 px-2.5 py-0.5 rounded-lg flex items-center gap-2">
                  <span className="text-gray-400">Memory</span>
                  <span className="font-bold text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded text-[10px]">
                    1 OK
                  </span>
                </div>
              );
            }
            const isHigh = mem > 90;
            return (
              <div className="bg-gray-900/60 border border-gray-700 px-2.5 py-0.5 rounded-lg flex items-center gap-2">
                <span className="text-gray-400">Memory</span>
                <span
                  className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${
                    isHigh
                      ? 'text-red-400 bg-red-500/15 border border-red-500/30'
                      : 'text-green-400 bg-green-500/10'
                  }`}
                >
                  {mem.toFixed(1)}%
                </span>
              </div>
            );
          })()}

          <div className="bg-gray-900/60 border border-gray-700 px-2.5 py-0.5 rounded-lg flex items-center gap-2">
            <span className="text-gray-400">Connections</span>
            <span className="font-bold text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded text-[10px]">
              {metricsData?.connections_active ?? 0} / {metricsData?.connections_max ?? 100}
            </span>
          </div>

          {(() => {
            const userVal = metricsData?.host_cpu_user_pct;
            const sysVal = metricsData?.host_cpu_sys_pct;
            const user = userVal !== undefined && userVal !== null ? Number(userVal) : NaN;
            const sys = sysVal !== undefined && sysVal !== null ? Number(sysVal) : NaN;
            const cpuTotal = isNaN(user) && isNaN(sys) ? 0 : (isNaN(user) ? 0 : user) + (isNaN(sys) ? 0 : sys);
            const mem = metricsData?.host_mem_used_pct ? Number(metricsData.host_mem_used_pct) : 0;
            const isDegraded = cpuTotal > 90 || mem > 90;

            return (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-gray-400">Cluster Status:</span>
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                    isDegraded
                      ? 'bg-red-500/10 text-red-400 border-red-500/30'
                      : 'bg-green-500/10 text-green-400 border-green-500/20'
                  }`}
                >
                  {isDegraded ? 'WARNING' : 'HEALTHY'}
                </span>
              </div>
            );
          })()}
        </div>
      )}


      {/* Database Instances Summary Table */}
      {(() => {
        const effectiveDbType = dbType?.toLowerCase();
        const isOpenSearch = ['opensearch', 'elasticsearch'].includes(effectiveDbType || '');
        const isMongo = ['mongodb', 'mongo', 'mongod', 'documentdb'].includes(effectiveDbType || '')

        console.log(isMongo)

        return (
          <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-5 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-semibold text-white">Database Instances Summary</h3>
            </div>
            <div className="overflow-x-auto custom-scroll">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  {isOpenSearch ? (
                    <tr className="border-b border-gray-700/60 text-[11px] text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap">
                      <th className="py-2.5 px-3">Index / Database</th>
                      <th className="py-2.5 px-3 text-center">Health</th>
                      <th className="py-2.5 px-3 text-right">Size</th>
                      <th className="py-2.5 px-3 text-right">Docs Count</th>
                      <th className="py-2.5 px-3 text-right">Docs Deleted</th>
                      <th className="py-2.5 px-3 text-right">Primary Shards</th>
                      <th className="py-2.5 px-3 text-right">Replica Shards</th>
                      <th className="py-2.5 px-3 text-right">Segments</th>
                      <th className="py-2.5 px-3 text-right">Searches/s</th>
                      <th className="py-2.5 px-3 text-right">Indexing/s</th>
                      <th className="py-2.5 px-3 text-right">Gets/s</th>
                      <th className="py-2.5 px-3 text-right">Cache Hit</th>
                    </tr>
                  ) : isMongo ? (
                    <tr className="border-b border-gray-700/60 text-[11px] text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap">
                      <th className="py-2.5 px-3">Collection / Database</th>
                      <th className="py-2.5 px-3">DB Name</th>
                      <th className="py-2.5 px-3 text-center">Health</th>
                      <th className="py-2.5 px-3 text-right">Docs Count</th>
                      <th className="py-2.5 px-3 text-right">Docs Deleted</th>
                      <th className="py-2.5 px-3 text-right">Data Size</th>
                      <th className="py-2.5 px-3 text-right">Storage Size</th>
                      <th className="py-2.5 px-3 text-right">Index Size</th>
                      <th className="py-2.5 px-3 text-right">Total Size</th>
                      <th className="py-2.5 px-3 text-right">Indexes</th>
                      <th className="py-2.5 px-3 text-right">Index Status</th>
                      <th className="py-2.5 px-3 text-center">Is Capped</th>
                      <th className="py-2.5 px-3 text-right">Avg Obj Size</th>
                      <th className="py-2.5 px-3 text-right">Cache Hit %</th>
                      <th className="py-2.5 px-3 text-right">Segments</th>
                      <th className="py-2.5 px-3 text-right">Primary Shards</th>
                      <th className="py-2.5 px-3 text-right">Replica Shards</th>
                      <th className="py-2.5 px-3 text-right">Searches/s</th>
                      <th className="py-2.5 px-3 text-right">Indexing/s</th>
                      <th className="py-2.5 px-3 text-right">Gets/s</th>
                      <th className="py-2.5 px-3 text-right">Last Seen</th>
                    </tr>
                  ) : (
                    <tr className="border-b border-gray-700/60 text-[11px] text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap">
                      <th className="py-2.5 px-3">Database</th>
                      <th className="py-2.5 px-3 text-right">Size</th>
                      <th className="py-2.5 px-3 text-right">Connections</th>
                      <th className="py-2.5 px-3 text-right">Open Txns</th>
                      <th className="py-2.5 px-3 text-right">Cache Hit</th>
                      <th className="py-2.5 px-3 text-right">Commits/s</th>
                      <th className="py-2.5 px-3 text-right">Rollbacks/s</th>
                      <th className="py-2.5 px-3 text-right">Disk Reads/s</th>
                      <th className="py-2.5 px-3 text-right">Buffer Hits/s</th>
                      <th className="py-2.5 px-3 text-right">Live Tuples</th>
                      <th className="py-2.5 px-3 text-right">Dead Tuples</th>
                      <th className="py-2.5 px-3 text-right">Autovacuum</th>
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-gray-700/30 text-gray-200">
                  {overviewDbTable && overviewDbTable.length > 0 ? (
                    overviewDbTable.map((db: any, idx: number) => {
                      if (isOpenSearch) {
                        const renderHealthBadge = (health?: string) => {
                          if (!health) return <span className="text-gray-400">-</span>;
                          const h = health.toLowerCase();
                          let bg = 'bg-gray-500/15 text-gray-300 border-gray-500/30';
                          if (h === 'green') bg = 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
                          else if (h === 'yellow') bg = 'bg-amber-500/15 text-amber-400 border-amber-500/30';
                          else if (h === 'red') bg = 'bg-rose-500/15 text-rose-400 border-rose-500/30';

                          return (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider ${bg}`}>
                              {health}
                            </span>
                          );
                        };

                        return (
                          <tr key={idx} className="hover:bg-gray-800/50 transition-colors font-mono whitespace-nowrap">
                            <td className="py-2.5 px-3 font-semibold text-white">{db.database}</td>
                            <td className="py-2.5 px-3 text-center">{renderHealthBadge(db.health)}</td>
                            <td className="py-2.5 px-3 text-right text-gray-300">
                              {db.size_mb != null ? `${Number(db.size_mb).toFixed(2)} MB` : "-"}
                            </td>
                            <td className="py-2.5 px-3 text-right text-blue-400 font-bold">
                              {db.docs_count != null ? Number(db.docs_count).toLocaleString() : 0}
                            </td>
                            <td className="py-2.5 px-3 text-right text-amber-400">
                              {db.docs_deleted != null ? Number(db.docs_deleted).toLocaleString() : 0}
                            </td>
                            <td className="py-2.5 px-3 text-right text-purple-400">{db.primary_shards ?? 0}</td>
                            <td className="py-2.5 px-3 text-right text-cyan-400">{db.replica_shards ?? 0}</td>
                            <td className="py-2.5 px-3 text-right text-gray-300">{db.segments_count ?? 0}</td>
                            <td className="py-2.5 px-3 text-right text-green-400">
                              {db.searches_per_s != null ? Number(db.searches_per_s).toFixed(2) : '0.00'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-emerald-400">
                              {db.indexing_per_s != null ? Number(db.indexing_per_s).toFixed(2) : '0.00'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-teal-400">
                              {db.gets_per_s != null ? Number(db.gets_per_s).toFixed(2) : '0.00'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-emerald-400 font-bold">
                              {db.cache_hit_pct != null ? `${Number(db.cache_hit_pct).toFixed(1)}%` : "-"}
                            </td>
                          </tr>
                        );
                      }

                      if (isMongo) {
                        const renderHealthBadge = (h?: string | null) => {
                          if (!h) return <span className="text-gray-500">-</span>;
                          const hl = h.toLowerCase();
                          let bg = 'bg-gray-500/15 text-gray-300 border-gray-500/30';
                          if (hl === 'green' || hl === 'healthy' || hl === 'ok') bg = 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
                          else if (hl === 'yellow' || hl === 'warning') bg = 'bg-amber-500/15 text-amber-400 border-amber-500/30';
                          else if (hl === 'red' || hl === 'critical') bg = 'bg-rose-500/15 text-rose-400 border-rose-500/30';

                          return (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider ${bg}`}>
                              {h}
                            </span>
                          );
                        };

                        const formatLastSeen = (isoStr?: string) => {
                          if (!isoStr) return '-';
                          const d = new Date(isoStr);
                          if (isNaN(d.getTime())) return isoStr;
                          return d.toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          });
                        };

                        return (
                          <tr key={idx} className="hover:bg-gray-800/50 transition-colors font-mono whitespace-nowrap">
                            <td className="py-2.5 px-3 font-semibold text-white">{db.collection || db.database || '—'}</td>
                            <td className="py-2.5 px-3 text-gray-400">{db.db_name || '—'}</td>
                            <td className="py-2.5 px-3 text-center">{renderHealthBadge(db.health)}</td>
                            <td className="py-2.5 px-3 text-right text-blue-400 font-bold">
                              {db.docs_count != null ? Number(db.docs_count).toLocaleString() : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-amber-400">
                              {db.docs_deleted != null ? Number(db.docs_deleted).toLocaleString() : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-gray-300">{fmtSize(db.size_mb)}</td>
                            <td className="py-2.5 px-3 text-right text-cyan-300">{fmtSize(db.storage_size_mb)}</td>
                            <td className="py-2.5 px-3 text-right text-purple-400">{fmtSize(db.index_size_mb)}</td>
                            <td className="py-2.5 px-3 text-right text-orange-300 font-bold">{fmtSize(db.total_size_mb)}</td>
                            <td className="py-2.5 px-3 text-right text-amber-400 font-bold">{db.index_count != null ? db.index_count : '-'}</td>
                            <td className="py-2.5 px-3 text-right text-gray-400">{db.index_status || '-'}</td>
                            <td className="py-2.5 px-3 text-center">
                              {db.is_capped != null ? (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider ${
                                  db.is_capped ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-gray-700/40 text-gray-400 border-gray-600/40'
                                }`}>
                                  {db.is_capped ? 'Yes' : 'No'}
                                </span>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-right text-gray-400">
                              {db.avg_obj_size_b != null ? `${db.avg_obj_size_b} B` : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-emerald-400 font-bold">
                              {db.cache_hit_pct != null ? `${Number(db.cache_hit_pct).toFixed(1)}%` : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-gray-300">{db.segments_count ?? '-'}</td>
                            <td className="py-2.5 px-3 text-right text-purple-400">{db.primary_shards ?? '-'}</td>
                            <td className="py-2.5 px-3 text-right text-cyan-400">{db.replica_shards ?? '-'}</td>
                            <td className="py-2.5 px-3 text-right text-green-400">
                              {db.searches_per_s != null ? Number(db.searches_per_s).toFixed(2) : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-emerald-400">
                              {db.indexing_per_s != null ? Number(db.indexing_per_s).toFixed(2) : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-teal-400">
                              {db.gets_per_s != null ? Number(db.gets_per_s).toFixed(2) : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-gray-400 text-[10px]">
                              {formatLastSeen(db.last_seen)}
                            </td>
                          </tr>
                        );
                      }

                      const formatVacuumAge = (sec: number | null | undefined) => {
                        if (!sec || sec === 0) return 'Never';
                        const mins = Math.floor(sec / 60);
                        const hours = Math.floor(sec / 3600);
                        const days = Math.floor(sec / 86400);
                        if (days > 0) return `${days}d ago`;
                        if (hours > 0) return `${hours}h ago`;
                        if (mins > 0) return `${mins}m ago`;
                        return `${sec.toFixed(0)}s ago`;
                      };

                      return (
                        <tr key={idx} className="hover:bg-gray-800/50 transition-colors font-mono whitespace-nowrap">
                          <td className="py-2.5 px-3 font-semibold text-white">{db.database}</td>
                          <td className="py-2.5 px-3 text-right text-gray-300">
                            {db.size_mb != null ? `${Number(db.size_mb).toFixed(2)} MB` : "-"}
                          </td>
                          <td className="py-2.5 px-3 text-right text-blue-400 font-bold">{db.connections ?? 0}</td>
                          <td className="py-2.5 px-3 text-right text-amber-400">{db.open_txns ?? 0}</td>
                          <td className="py-2.5 px-3 text-right text-emerald-400 font-bold">
                            {db.cache_hit_pct != null ? `${Number(db.cache_hit_pct).toFixed(1)}%` : "-"}
                          </td>
                          <td className="py-2.5 px-3 text-right text-green-400">{db.commits_per_s ?? 0}</td>
                          <td className="py-2.5 px-3 text-right text-red-400">{db.rollbacks_per_s ?? 0}</td>
                          <td className="py-2.5 px-3 text-right text-gray-300">{db.blks_read_per_s ?? 0}</td>
                          <td className="py-2.5 px-3 text-right text-gray-300">{db.blks_hit_per_s ?? 0}</td>
                          <td className="py-2.5 px-3 text-right text-gray-300">{db.live_tuples ?? 0}</td>
                          <td className="py-2.5 px-3 text-right text-gray-300">
                            {db.dead_tuples ?? 0}{' '}
                            <span className="text-[10px] text-gray-500 font-normal">
                              ({db.dead_tuples_pct != null ? `${Number(db.dead_tuples_pct).toFixed(2)}%` : '0%'})
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right text-gray-400">
                            {formatVacuumAge(db.last_autovacuum_age_sec)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={isMongo ? 21 : 12} className="py-8 text-center text-gray-400 italic">
                        No active database instances detected.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Main Large Chart: Average Load Sessions */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-3.5 shadow-xl backdrop-blur-sm">
        <div className="flex items-center justify-between mb-2.5 border-b border-gray-700/50 pb-2">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-400" />
            <h3 className="text-xs font-semibold text-white"> Active vs Total Sessions</h3>
          </div>

        </div>
        <div className="h-44 w-full flex items-center justify-center">
          {hasData(connDataFormatted) ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={connDataFormatted}>
                <defs>
                  <linearGradient id="colorActive" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', borderRadius: '0.5rem', color: '#fff', fontSize: '11px' }} />
                <Legend content={<ScrollableLegend hoveredSeries={hoveredConn} onHoverSeries={setHoveredConn} />} />
                <Area
                  type="monotone"
                  dataKey="connections_active"
                  name="Active Sessions"
                  stroke="#10b981"
                  strokeOpacity={getOpacity(hoveredConn, 'connections_active')}
                  fillOpacity={getOpacity(hoveredConn, 'connections_active') * 0.4}
                  fill="url(#colorActive)"
                  strokeWidth={hoveredConn === 'connections_active' ? 2.5 : 1.5}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="connections_total"
                  name="Total Sessions"
                  stroke="#3b82f6"
                  strokeOpacity={getOpacity(hoveredConn, 'connections_total')}
                  strokeWidth={hoveredConn === 'connections_total' ? 2.5 : 1.5}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <span className="text-xs text-gray-500 italic">No session load telemetry available for selected timeframe</span>
          )}
        </div>
      </div>

      {/* 3 Columns Subcharts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-800/40 border border-gray-700/60 rounded-xl p-3 shadow-xl">
        {/* CPU Utilization */}
        {/* <div className="">
          <div className="flex items-center gap-2 mb-2 border-b border-gray-700/50 pb-1.5">
            <Cpu className="w-3.5 h-3.5 text-purple-400" />
            <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">CPU Utilization %</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(hostDataFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hostDataFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} domain={[0, 100]} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredCpu} onHoverSeries={setHoveredCpu} />} />
                  <Line
                    type="linear"
                    dataKey="host_cpu_user_pct"
                    name="User %"
                    stroke="#3b82f6"
                    strokeOpacity={getOpacity(hoveredCpu, 'host_cpu_user_pct')}
                    strokeWidth={hoveredCpu === 'host_cpu_user_pct' ? 2.5 : 1.5}
                    dot={(props: any) => renderSpikeDot(props, hostDataFormatted, 'host_cpu_user_pct', '#3b82f6', '%')}
                  />
                  <Line
                    type="linear"
                    dataKey="host_cpu_sys_pct"
                    name="System %"
                    stroke="#ef4444"
                    strokeOpacity={getOpacity(hoveredCpu, 'host_cpu_sys_pct')}
                    strokeWidth={hoveredCpu === 'host_cpu_sys_pct' ? 2.5 : 1.5}
                    dot={(props: any) => renderSpikeDot(props, hostDataFormatted, 'host_cpu_sys_pct', '#ef4444', '%')}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No CPU data available</span>
            )}
          </div>
        </div> */}

        {/* Query Throughput */}
        <div className="">
          <div className="flex items-center gap-2 mb-2 border-b border-gray-700/50 pb-1.5">
            <Layers className="w-3.5 h-3.5 text-green-400" />
            <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">Query Throughput (by Type)</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(throughputFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={throughputFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredThroughput} onHoverSeries={setHoveredThroughput} />} />
                  {Object.keys(throughputFormatted[0] || {}).filter((k) => k !== 'time').map((key, i) => {
                    const color = colors[i % colors.length];
                    return (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredThroughput, key)}
                        strokeWidth={hoveredThroughput === key ? 2.5 : 1.5}
                        dot={false}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No query throughput data available</span>
            )}
          </div>
        </div>

        {/* Average Duration */}
        <div className="">
          <div className="flex items-center gap-2 mb-2 border-b border-gray-700/50 pb-1.5">
            <HardDrive className="w-3.5 h-3.5 text-amber-400" />
            <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">Average Duration (by Type)</h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(durationFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={durationFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredDuration} onHoverSeries={setHoveredDuration} />} />
                  {Object.keys(durationFormatted[0] || {}).filter((k) => k !== 'time').map((key, i) => {
                    const color = colors[(i + 2) % colors.length];
                    return (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={color}
                        strokeOpacity={getOpacity(hoveredDuration, key)}
                        strokeWidth={hoveredDuration === key ? 2.5 : 1.5}
                        dot={false}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No query duration data available</span>
            )}
          </div>
        </div>
      </div>


    </div>
  );
};
